import { format, isSameYear } from "date-fns";
import { useContext } from "react";

import { TimeLocale } from "helpers/AppProvider.types";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

export interface IDateDisplayFormattingProps {
    showDate: boolean;
    showTime: boolean;
    showDayOfWeek: boolean;
    hideCurrentYear?: boolean;
}

/**
 * Format the date as a string
 * @param value
 * @param builderTimeLocale pass the builders current time locale. This is stored in react context, grab with withTimezoneDetails
 * @param showDate Shows the date (Month, Day, Year)
 * @param showTime Shows the time, will appear after date
 * @param showDayOfWeek Shows the day of week (Mon, Tue, Wed...) at the beginning of the date
 * @param hideCurrentYear Optional: Hide the current year if the date is in the current year
 */
export function formatDate(
    value: Date,
    builderTimeLocale: TimeLocale,
    { showDate, showTime, showDayOfWeek, hideCurrentYear = false }: IDateDisplayFormattingProps,
): string {
    if (value === null || value === undefined) {
        throw Error(`formatDate() was passed a value that was set to "${value}"`);
    }
    if (!showDate && !showTime && !showDayOfWeek) {
        throw Error("One of showDate, showTime, or showDayOfWeek must be true");
    }

    let dateFormat = "";

    if (showDate) {
        const date = hideCurrentYear && isSameYear(value, new Date()) ? `MMM d` : `MMM d, yyyy`;
        dateFormat = date;
    }

    if (showTime) {
        const spacer = dateFormat === "" ? "" : ", ";
        // 24 hour format uses 24hour:minute - 12 hour format uses 12hour:minute AM/PM
        const time = builderTimeLocale.is24HourClock ? "HH:mm" : "h:mm aa";
        dateFormat = dateFormat + spacer + time;
    }

    if (showDayOfWeek) {
        const dayOfWeek = "EEE";
        const spacer = dateFormat === "" ? "" : ", ";
        dateFormat = dayOfWeek + spacer + dateFormat;
    }

    return format(value, dateFormat);
}

/**
 * Hook that returns `formatDate` with the current builder's time locale
 * @returns A function that formats a date as a string with the current builder's time locale
 */
export function useFormatDate() {
    const builderInfo = useContext(BuilderInfoContext);
    const builderTimeLocale = builderInfo?.locale.timeLocale ?? TimeLocale.default;

    return (value: Date, options: IDateDisplayFormattingProps) =>
        formatDate(value, builderTimeLocale, options);
}
